<template>
  <div class="zoox-component zoox-main-layout">
    <!--Evento emitido ao clicar em uma opção do menu
        @event click
        @property {Object} menuOption - Valor da opção selecionada
     -->
    <z-navigation-drawer
      ref="navDrawer"
      v-bind="navDrawerProps"
      :dark="dark"
      :absolute="absolute"
      :hide-logo="hideNavLogo"
      :menu-options="menuOptions"
      @click="$emit('click', $event)"
      @click:logo="onClickLogo"
    />
    <v-main
      id="zoox-content"
      :style="{
        'padding-left': `${navDrawerWidth}px`,
        'background-color': background,
      }"
      class="zoox-content"
    >
      <z-header
        v-if="!hideHeader"
        :dark="dark"
        :lang="lang"
        :username="username"
        :user-email="userEmail"
        :company="company"
        :product="product"
        :lang-options="langOptions"
        :src-avatar="srcAvatar"
        :avatar-menu-options="avatarMenuOptions"
        @logout="onClickLogout"
        @change-lang="onChangeLang"
        @click="onClickAvatarOption($event)"
      >
        <template v-if="$scopedSlots['product']" v-slot:product>
          <!--@slot Slot para customizar a area de produto do componente -->
          <slot name="product"></slot>
        </template>
        <!--@slot Slot para customizar a area de toolbar do componente -->
        <slot name="toolbar" />
        <template v-if="$scopedSlots['username']" #username>
          <slot name="username" />
        </template>
      </z-header>
      <!--@slot Slot do conteúdo principal do componente -->
      <slot name="content" />
    </v-main>
  </div>
</template>

<script>
/**
 * <codesandbox-link link="https://codesandbox.io/s/zmainlayout-exemplo-pb6uz"></codesandbox-link>
 *
 * Componente de fachada que agrega z-header, z-nav-drawer e adiciona um slot para o conteúdo
 *
 * @displayName Main Layout
 */
export default {
  name: 'ZMainLayout',
  props: {
    /**
     * Nome do produto que está sendo desenvolvido
     */
    product: {
      type: String,
      required: true,
    },
    /**
     * Linguagens disponíveis no select. Utilizar somente
     * en-pt-es.
     */
    langOptions: {
      type: Array,
      required: false,
      default: () => ['pt', 'en'],
    },
    /**
     * Define as opções do menu lateral
     *
     * @typedef Object {title:string,icon:string,isActive:boolean,options:array}
     */
    menuOptions: {
      type: Array,
      required: true,
    },
    /**
     * Nome do usuário logado para colocar no header
     */
    username: {
      type: String,
      required: true,
    },
    /**
     * Linguagem selecionada
     */
    lang: {
      type: String,
      required: false,
    },
    /**
     * Caminho da imagem, caso queria adicionar um avatar
     */
    srcAvatar: {
      type: [Object, String],
      required: false,
    },
    /**
     * Flag para deixar o nav drawer com position absolute
     */
    absolute: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para esconder a logo do navigation drawer
     */
    hideNavLogo: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para indicar para esconder o header
     */
    hideHeader: {
      type: Boolean,
      default: false,
    },
    /**
     * Props para serem passadas para o z-nav-drawer
     */
    navDrawerProps: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Empresa que o produto está
     */
    company: {
      type: String,
      default: '',
    },
    /**
     * Email do usuário
     */
    userEmail: {
      type: String,
      default: '',
    },
    /**
     * Cor do background do conteúdo do layout
     */
    background: {
      type: String,
      default: 'var(--background)',
    },
    /**
     * Flag para indicar que o fundo é dark. Usa textos em branco
     */
    dark: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {navDrawerWidth: 0}
  },
  mounted() {
    this.navDrawerWidth = this.$refs.navDrawer.$el.offsetWidth
  },
  methods: {
    onChangeLang(lang) {
      /**
       * Evento emitido para alterar a linguagem
       *
       * @property {String} lang - Linguagem selecionada
       */
      this.$emit('change-lang', lang)
    },
    onClickLogout() {
      /**
       * Evento emitido para fazer logout da aplicação
       */
      this.$emit('logout')
    },
    onClickLogo() {
      /**
       * Evento disparado quando usuário clica na logo do menu
       */
      this.$emit('click:logo')
    },
    onClickAvatarOption(id) {
      /**
       * Emite evento das opções do Avatar Menu
       */
      this.$emit('click:option', id)
    },
  },
  i18n: {
    messages: {
      pt: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Logout',
      },
      es: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Logout',
      },
      en: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Logout',
      },
      fr: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Déconnexion',
      },
      tr: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Çıkış',
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.zoox-main-layout {
  position: relative;
  display: flex;
  height: 100%;
}

.zoox-content {
  height: 100%;
}
</style>
